import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Instagram, Youtube, MessageCircle, Menu, X } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSettings } from "../lib/queries";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { data: settings } = useSettings();
  const { scrollY } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  useEffect(() => setOpen(false), [location.pathname]);

  const nav = [
    { label: t("nav_home"), to: "/#top" },
    { label: t("nav_services"), to: "/#services" },
    { label: t("nav_projects"), to: "/#projects" },
    { label: lang === "tr" ? "Viral" : "Viral", to: "/#viral" },
    { label: t("nav_about"), to: "/#about" },
    { label: t("nav_references"), to: "/#testimonials" },
    { label: t("nav_contact"), to: "/#contact" },
  ];

  const handleNav = (to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else navigate(to);
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          backdropFilter: scrolled ? "blur(22px)" : "blur(0px)",
          WebkitBackdropFilter: scrolled ? "blur(22px)" : "blur(0px)",
          backgroundColor: scrolled ? "rgba(8,8,13,0.72)" : "rgba(8,8,13,0)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
        }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-[#A855F7] shadow-[0_0_16px_rgba(168,85,247,0.9)]" />
            <span className="font-display text-[0.95rem] tracking-[0.18em] uppercase">
              İxirli
            </span>
            <span className="hidden sm:inline text-xs text-white/50 tracking-widest uppercase">
              Production
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {nav.map((n) => (
              <button
                key={n.label}
                data-testid={`nav-${n.label}`}
                onClick={() => handleNav(n.to)}
                className="text-[13px] tracking-wide text-white/70 hover:text-white transition-colors uppercase font-medium"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "tr" ? "en" : "tr")}
              className="text-xs tracking-widest uppercase text-white/60 hover:text-white transition-colors border border-white/10 rounded-full px-3 py-1.5 font-mono-ui"
            >
              {lang === "tr" ? "EN" : "TR"}
            </button>
            <a
              data-testid="ig-link"
              href={settings?.instagram || "https://instagram.com/ixirli_"}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full border border-white/10 text-white/70 hover:text-white hover:border-[#A855F7]/60 transition-colors"
            >
              <Instagram size={15} />
            </a>
            <a
              data-testid="yt-link"
              href={settings?.youtube || "#"}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full border border-white/10 text-white/70 hover:text-white hover:border-[#A855F7]/60 transition-colors"
            >
              <Youtube size={15} />
            </a>
            <a
              data-testid="wa-link"
              href={`https://wa.me/${(settings?.whatsapp || "+905337819108").replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full border border-white/10 text-white/70 hover:text-white hover:border-[#A855F7]/60 transition-colors"
            >
              <MessageCircle size={15} />
            </a>
            <button
              data-testid="header-cta"
              onClick={() => handleNav("/#contact")}
              className="hidden md:inline-flex btn-primary py-2.5 px-5 text-[11px]"
            >
              {t("cta_quote")}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed inset-x-0 top-20 z-40 bg-[#08080D]/95 backdrop-blur-xl border-b border-white/10 lg:hidden"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {nav.map((n) => (
              <button
                key={n.label}
                onClick={() => handleNav(n.to)}
                className="text-left text-2xl font-display uppercase tracking-tight text-white hover:text-[#A855F7] transition-colors"
              >
                {n.label}
              </button>
            ))}
            <button onClick={() => handleNav("/#contact")} className="btn-primary mt-4 w-fit">
              {t("cta_quote")}
            </button>
          </div>
        </motion.div>
      )}
    </>
  );
}
